// ══════════════════════════════════════════════════════════
//  #FLEET_RIG_FILE_V1 (2026-10-09) — the rig file: what opens when a rig tile is clicked.
//
//  Bill: "Fleet new tiles for RIGS API from Vermeer landed nicely however clicking the tile takes you into our
//  equipment master file and need to dive into diagnosis or Maintenance items."
//
//  One screen per rig, two halves:
//    DIAGNOSIS    open fault codes with the code text, the codes that came before them, every change the
//                 15-minute fault watch logged, factory bulletins, and what the rig is doing right now.
//    MAINTENANCE  DOPE's own Service Due against the factory hour meter, the maker's service log, whether the
//                 rig is in the shop today, its past shop visits and its service history.
//  Buttons at the bottom jump to the working screens (Maintenance to log a service, In Shop, Weekly Inventory).
//
//  Read-only. Nothing here writes anywhere. Everything shown is already on file — the feed record
//  (`fleet-oem-hours`), `fleet-service`, `fleet-service-log`, the In Shop list and Shop History.
//  NOTE: the feed keeps code text only while a code is open, so a rig with nothing open has no code list.
// ══════════════════════════════════════════════════════════
import { useEffect, useState } from "react";
import { oemLabel, mdy } from "./oemFeed";
import { svcSummary, svcNormDos } from "./fleetService";
import { unitKey } from "./unitValid";
import { UnitWorkHistory } from "./WorkOrders";   // #FLEET_WO_TABLES_V1

const BAD = '#F87171', NOTE = '#7DD3FC';
const hrsFmt = (n) => (n == null || n === '' || isNaN(Number(n)) ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 1 }));
const intFmt = (n) => (n == null || n === '' || isNaN(Number(n)) ? '—' : Math.round(Number(n)).toLocaleString('en-US'));
const whenFmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('en-US', { weekday: 'short' })} ${d.getMonth() + 1}/${d.getDate()} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
};
// a calendar date (YYYY-MM-DD) as M/D/YY with no time-zone math
const dayShort = (v) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || '')); return m ? `${Number(m[2])}/${Number(m[3])}/${m[1].slice(2)}` : String(v || ''); };
const barDay = (v) => { const m = /^\d{4}-(\d{2})-(\d{2})/.exec(String(v || '')); return m ? `${Number(m[1])}/${Number(m[2])}` : ''; };
// "AMBER:1,NOTIFICATION:2" (how the fault watch stores a rig's open codes) → "1 amber, 2 notification"
const sigText = (sig) => {
  const parts = String(sig || '').split(',').map(p => { const i = p.lastIndexOf(':'); return i > 0 ? `${p.slice(i + 1)} ${p.slice(0, i).toLowerCase()}` : ''; }).filter(Boolean);
  return parts.length ? parts.join(', ') : 'none';
};
// one stored code → its parts, whatever the maker calls the fields (same picking rule as oemCodeLines)
const codeParts = (f) => {
  if (!f || typeof f !== 'object') return null;
  const keys = Object.keys(f);
  const pick = (re) => { const k = keys.find(x => re.test(x) && f[x] !== '' && f[x] != null && typeof f[x] !== 'boolean'); return k ? String(f[k]) : ''; };
  const spn = pick(/^spn$/i), fmi = pick(/^fmi$/i);
  const code = spn ? `SPN ${spn}${fmi ? ' / FMI ' + fmi : ''}` : pick(/code|dtc/i);
  const desc = pick(/desc|text|message/i);
  if (!code && !desc) return null;
  return { code, desc, sev: pick(/sever|level|lamp/i).toUpperCase(), at: f.at || '' };
};

export default function FleetRigFile({ c, name, pulledAt, theme, shopRow, shopHistory, sg, onClose, onPrev, onNext, onOpenMaint, onOpenInShop, onOpenWeekly }) {
  const T = theme || {};
  const ACCENT = T.accent || '#FF7A1A', WARN = T.accent2 || '#FFB020', GOOD = T.good || '#4ADE80', STEEL = T.steel || '#9AA0A8', TEXT = T.text || '#EDEEF0';
  const LINE = T.panelBorder || '#3A3D42', PANEL = T.panel || '#22252A';
  const toneColor = { bad: BAD, warn: WARN, ok: GOOD, none: STEEL, note: NOTE };
  const sevColor = (s) => (/RED|STOP|CRIT|SEVERE|HIGH/.test(s) ? BAD : (/AMBER|YELLOW|WARN|MED/.test(s) ? WARN : (s ? NOTE : STEEL)));

  const { x, d, act, split, wk, bars, svc, own, flt, fltNew, mapUrl } = c;
  const uk = x.uk || unitKey(x.r.unit);
  const maker = oemLabel(d.provider);
  const isSilent = act.state === 'silent';

  // Esc closes, ← → flip to the neighbouring rig; the page behind does not scroll while the file is open.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose && onClose();
      else if (e.key === 'ArrowLeft' && onPrev) onPrev();
      else if (e.key === 'ArrowRight' && onNext) onNext();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [onClose, onPrev, onNext]);

  // service history is its own record; read it when the file opens (null = still reading)
  const [log, setLog] = useState(null);
  useEffect(() => {
    let dead = false;
    setLog(null);
    (async () => {
      try { const l = sg ? await sg('fleet-service-log') : null; if (!dead) setLog(l && Array.isArray(l.entries) ? l.entries.filter(e => e && e.uk === uk) : []); }
      catch { if (!dead) setLog([]); }
    })();
    return () => { dead = true; };
  }, [sg, uk]);

  // ── what the rig is doing ──
  const ws = act.state === 'active' ? String(d.workingStatus || '').toUpperCase() : '';
  const nowWorking = /WORK/.test(ws), nowIdling = /IDL/.test(ws);
  const pillColor = act.state === 'active' ? (nowIdling ? WARN : GOOD) : (act.state === 'parked' ? WARN : STEEL);
  const pillText = act.state === 'active' ? (nowWorking ? 'WORKING' : (nowIdling ? 'IDLING' : 'RAN TODAY')) : (act.state === 'parked' ? `PARKED ${act.days} DAY${act.days === 1 ? '' : 'S'}` : 'TRACKER SILENT');

  // ── diagnosis ──
  const codes = (Array.isArray(d.faultCodes) ? d.faultCodes : []).map(codeParts).filter(Boolean);
  const openN = Math.min(flt.total || 0, codes.length);
  const changes = (Array.isArray(d.faultLog) ? d.faultLog : []).slice().reverse();
  const camp = d.campaigns || null;
  const campMand = camp ? (Number(camp.mandatory) || 0) : 0, campOpen = camp ? campMand + (Number(camp.optional) || 0) : 0;
  const rod = d.rod || null;

  // ── maintenance ──
  const ownTone = !own ? 'none' : (own.state === 'overdue' ? 'bad' : (own.state === 'soon' ? 'warn' : (own.state === 'ok' ? 'ok' : 'none')));
  const ownText = own ? svcSummary(own, x.serviceRule) : '';
  const pm = d.pm || null;
  const visits = (Array.isArray(shopHistory) ? shopHistory : []).filter(r => r && unitKey(r.unit) === uk)
    .slice().sort((a, b) => String(b.dateOut || b.dateIn || '').localeCompare(String(a.dateOut || a.dateIn || '')));
  const warDays = d.warrantyEnd ? Math.round((new Date(d.warrantyEnd + 'T12:00:00Z').getTime() - Date.now()) / 86400000) : null;
  const history = (log || []).slice().sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));

  // ── styles ──
  const lbl = { fontSize: 9.5, fontWeight: 900, letterSpacing: .7, textTransform: 'uppercase', color: STEEL };
  const half = { fontSize: 11, fontWeight: 900, letterSpacing: 1.2, textTransform: 'uppercase', color: ACCENT, marginBottom: 8 };
  const block = { background: PANEL, border: `1px solid ${LINE}`, borderRadius: 10, padding: '11px 13px', marginBottom: 10 };
  const big = { fontSize: 15, fontWeight: 900, lineHeight: 1.25, marginTop: 3 };
  const small = { fontSize: 11.5, color: TEXT, lineHeight: 1.5 };
  const muted = { fontSize: 11, color: STEEL, lineHeight: 1.5 };
  const row = { display: 'flex', gap: 8, alignItems: 'baseline', padding: '6px 0', borderTop: `1px solid ${LINE}` };
  const chip = (col) => ({ flex: '0 0 auto', fontSize: 8.5, fontWeight: 900, letterSpacing: .6, color: col, border: `1px solid ${col}`, borderRadius: 3, padding: '1px 5px', whiteSpace: 'nowrap' });
  const btn = { cursor: 'pointer', background: 'transparent', color: ACCENT, border: `1px solid ${ACCENT}88`, borderRadius: 7, padding: '6px 12px', fontSize: 11.5, fontWeight: 800, fontFamily: 'inherit' };
  const ghost = { ...btn, color: TEXT, border: `1px solid ${LINE}`, padding: '4px 10px' };
  const stat = { flex: '1 1 150px', minWidth: 0 };

  const codeRow = (p, i, isOpen) => (
    <div key={`${p.code}-${p.at}-${i}`} style={{ ...row, opacity: isOpen ? 1 : .78 }}>
      <span style={chip(sevColor(p.sev))}>{p.sev || 'CODE'}</span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12, color: TEXT }}><b>{p.code || 'Code'}</b>{p.desc ? ` — ${p.desc}${p.desc.length >= 139 ? '…' : ''}` : <span style={{ color: STEEL }}> — no description from {maker}</span>}</div>
        <div style={muted}>{p.at ? `logged ${whenFmt(p.at)}` : 'no date on it'}
          {p.code ? <span> · <a href={`https://www.google.com/search?q=${encodeURIComponent(`${maker} ${d.model || ''} ${p.code.replace(' / ', ' ')}`.replace(/\s+/g, ' '))}`}
            target="_blank" rel="noopener noreferrer" style={{ color: ACCENT, fontWeight: 800, textDecoration: 'none' }}>search this code ↗</a></span> : null}</div>
      </div>
    </div>
  );

  return (
    <div onClick={onClose} role="dialog" aria-modal="true" aria-label={`Rig file ${x.r.unit}`}
      style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(8,9,11,.84)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '3vh 12px' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 1040, background: '#17191C', border: `1px solid ${LINE}`, borderTop: `3px solid ${ACCENT}`, borderRadius: 14, color: TEXT,
          boxShadow: `0 0 60px -12px ${ACCENT}88`, padding: '14px 16px 16px' }}>

        {/* who it is */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: '1 1 260px' }}>
            <div style={{ ...lbl, color: ACCENT }}>Rig file</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 26, fontWeight: 900, color: ACCENT, letterSpacing: .3 }}>{x.r.unit}</span>
              <span style={{ fontSize: 13, color: TEXT, fontWeight: 700 }} title={d.model ? `${maker} model ${d.model}` : undefined}>{name}</span>
              <span style={{ ...chip(pillColor), boxShadow: act.state === 'active' ? `0 0 10px -2px ${pillColor}` : 'none' }}>{act.state === 'active' ? '● ' : ''}{pillText}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto' }}>
            {onPrev ? <button type="button" onClick={onPrev} style={ghost} title="Previous rig (←)">‹ Prev</button> : null}
            {onNext ? <button type="button" onClick={onNext} style={ghost} title="Next rig (→)">Next ›</button> : null}
            <button type="button" onClick={onClose} style={{ ...ghost, fontWeight: 900 }} title="Close (Esc)">✕ Close</button>
          </div>
        </div>

        {/* the four numbers, same order as the tile */}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '12px 0 14px', padding: '10px 12px', background: PANEL, border: `1px solid ${LINE}`, borderRadius: 10 }}>
          <div style={stat}>
            <div style={lbl}>Hour meter</div>
            <div style={big}>{hrsFmt(d.engineHours)} <span style={{ fontSize: 11, color: STEEL, fontWeight: 600 }}>hrs</span></div>
            <div style={muted}>{isSilent ? (act.at ? `last heard ${mdy(String(act.at).slice(0, 10))}` : 'never heard from') : `last heard ${whenFmt(act.at)}`}
              {mapUrl && !isSilent ? <span> · <a href={mapUrl} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT, fontWeight: 800, textDecoration: 'none' }}>📍 map</a></span> : null}</div>
          </div>
          <div style={stat}>
            <div style={lbl}>Idling</div>
            <div style={{ ...big, color: split.idlePct == null ? STEEL : WARN }}>{split.idlePct == null ? '—' : `${split.idlePct}%`}</div>
            <div style={muted}>{split.idlePct == null ? 'no idle hours in the feed' : `${hrsFmt(split.idle)} of ${hrsFmt(split.total)} lifetime hrs`}</div>
          </div>
          <div style={stat}>
            <div style={lbl}>Service</div>
            <div style={{ ...big, color: toneColor[ownTone] }}>{!own ? '—' : (own.state === 'unknown' || !ownText ? 'Needs data' : ownText)}</div>
            <div style={muted}>{own && own.due != null ? `due at ${intFmt(own.due)} h · DOPE's Service Due` : 'no DOPE schedule for this unit'}</div>
          </div>
          <div style={stat}>
            <div style={lbl}>Fault codes</div>
            <div style={{ ...big, color: flt.known && !isSilent ? toneColor[flt.tone] : STEEL }}>{!flt.known || isSilent ? '—' : (flt.total ? `${flt.total} open` : 'None open')}</div>
            <div style={muted}>{isSilent ? 'tracker silent — not known' : (!flt.known ? `${maker} sends no fault data for it` : (flt.total ? (flt.text || '') : `checked ${whenFmt(pulledAt) || 'on the last pull'}`))}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, alignItems: 'start' }}>
          {/* ═════════ DIAGNOSIS ═════════ */}
          <div>
            <div style={half}>Diagnosis</div>

            <div style={{ ...block, borderLeft: `3px solid ${flt.known && !isSilent ? toneColor[flt.tone] : STEEL}` }}>
              <div style={lbl}>Fault codes — open on the rig</div>
              {isSilent ? (
                <div style={{ ...small, color: WARN, marginTop: 4 }}>⚠ The tracker on this rig is not sending, so nothing is known about its codes.</div>
              ) : !flt.known ? (
                <div style={{ ...small, color: STEEL, marginTop: 4 }}>{maker} sends no fault data for this machine.</div>
              ) : (
                <div>
                  <div style={{ ...big, color: toneColor[flt.tone] }}>
                    {flt.total ? `${flt.total} fault code${flt.total === 1 ? '' : 's'} open${flt.text ? ' — ' + flt.parts.map(pt => pt.sev.toLowerCase()).join(', ') : ''}` : 'No fault codes open'}
                    {fltNew ? <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 900, letterSpacing: .6, color: '#15171A', background: flt.tone === 'bad' ? BAD : WARN, borderRadius: 3, padding: '1px 6px', verticalAlign: 'middle' }}>NEW</span> : null}
                  </div>
                  {flt.total && d.faultSince ? <div style={muted}>DOPE first saw {flt.total === 1 ? 'it' : 'them'} {whenFmt(d.faultSince)}</div> : null}
                  {flt.total > 0 && !codes.length ? (
                    <div style={{ ...muted, marginTop: 6 }}>The code text is not on file yet — {maker}'s summary is only a count. The text is pulled from the rig's fault history on the next 15-minute check.</div>
                  ) : null}
                  {codes.slice(0, openN).map((p, i) => codeRow(p, i, true))}
                  {flt.total > codes.length && codes.length ? <div style={{ ...muted, marginTop: 4 }}>{flt.total - codes.length} more open than the history on file names.</div> : null}
                </div>
              )}
            </div>

            {codes.length > openN ? (
              <div style={block}>
                <div style={lbl}>Earlier codes on this rig</div>
                <div style={{ ...muted, marginBottom: 2 }}>From the rig's own fault history, newest first. Not open now.</div>
                {codes.slice(openN).map((p, i) => codeRow(p, i + openN, false))}
              </div>
            ) : null}

            <div style={block}>
              <div style={lbl}>Code changes DOPE has logged</div>
              {changes.length ? changes.map((l, i) => {
                const col = l.kind === 'cleared' ? GOOD : (l.kind === 'new' ? WARN : NOTE);
                return (
                  <div key={`${l.at}-${i}`} style={{ ...row, ...(i === 0 ? { borderTop: 'none' } : {}) }}>
                    <span style={chip(col)}>{l.kind === 'cleared' ? 'CLEARED' : (l.kind === 'new' ? 'NEW CODE' : 'CHANGED')}</span>
                    <div style={small}>{whenFmt(l.at)} <span style={{ color: STEEL }}>— {l.kind === 'cleared' ? `was ${sigText(l.from)}` : `now ${sigText(l.to)} (was ${sigText(l.from)})`}</span></div>
                  </div>
                );
              }) : <div style={{ ...muted, marginTop: 4 }}>No changes yet. The fault watch (started 10/8/26) logs every code that appears, changes or clears.</div>}
            </div>

            {campOpen > 0 ? (
              <div style={{ ...block, ...(campMand ? { borderLeft: `3px solid ${BAD}` } : {}) }}>
                <div style={lbl}>Factory bulletins</div>
                <div style={{ ...small, color: campMand ? BAD : TEXT, fontWeight: campMand ? 800 : 500, marginTop: 3 }}>{campOpen} open{campMand ? ` — ${campMand} mandatory` : ' — none mandatory'}</div>
                <div style={muted}>{maker} sends the count only. What each bulletin covers is in {maker}'s own site.</div>
              </div>
            ) : null}

            {!isSilent ? (
              <div style={block}>
                <div style={lbl}>What the rig has been doing</div>
                {split.idlePct != null ? (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ display: 'flex', height: 9, borderRadius: 5, overflow: 'hidden', background: '#15171A' }}>
                      <div style={{ width: `${100 - split.idlePct}%`, background: GOOD }} /><div style={{ width: `${split.idlePct}%`, background: WARN }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, marginTop: 3 }}>
                      <span style={{ color: GOOD, fontWeight: 700 }}>working {hrsFmt(split.work)} h</span><span style={{ color: WARN, fontWeight: 700 }}>idle {hrsFmt(split.idle)} h · {split.idlePct}%</span>
                    </div>
                  </div>
                ) : null}
                {wk ? <div style={{ ...small, marginTop: 6 }}>This week so far: <b>{hrsFmt(wk.run)} h</b> run{wk.idle != null ? <span style={{ color: STEEL }}> · {hrsFmt(wk.idle)} h of it idling</span> : null}</div> : null}
                {bars.length ? (
                  <div style={{ marginTop: 6 }}>
                    {bars.slice(-7).reverse().map(b => (
                      <div key={b.from} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5, padding: '2px 0' }}>
                        <span style={{ color: STEEL }}>{barDay(b.from)} → {barDay(b.to)}</span>
                        <span><b>{hrsFmt(b.run)} h</b> run{b.idle != null ? <span style={{ color: b.run > 0 && b.idle / b.run >= .5 ? WARN : STEEL }}> · {hrsFmt(b.idle)} h idling</span> : null}</span>
                      </div>
                    ))}
                  </div>
                ) : <div style={{ ...muted, marginTop: 6 }}>Day-by-day hours start once two daily readings are on file.</div>}
                {rod && rod.count != null && rod.count > 0 ? (
                  <div style={{ ...small, marginTop: 6 }}>⛏ Rod counter: <b>{intFmt(rod.count)} rod{rod.count === 1 ? '' : 's'}</b>{rod.stringLen ? ` · ${intFmt(rod.stringLen)} ft` : ''}{rod.at ? <span style={{ color: STEEL }}> · {whenFmt(rod.at)}</span> : null}
                    <div style={muted}>Last rod: max pull {rod.pullMax ? intFmt(rod.pullMax) : '—'} · max thrust {rod.thrustMax ? intFmt(rod.thrustMax) : '—'} <span title="Units are as the rig reports them — not yet checked against a rig display.">(as the rig reports)</span></div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* ═════════ MAINTENANCE ═════════ */}
          <div>
            <div style={half}>Maintenance</div>

            <div style={{ ...block, borderLeft: `3px solid ${toneColor[ownTone]}` }}>
              <div style={lbl}>Service due — DOPE</div>
              {own ? (
                <div>
                  <div style={{ ...big, color: toneColor[ownTone] }}>{ownText || 'On schedule'}{own.due != null ? <span style={{ color: TEXT, fontWeight: 700, fontSize: 12.5 }}> · due at {intFmt(own.due)} h</span> : null}</div>
                  <div style={{ ...small, marginTop: 4 }}>
                    Last serviced {x.serviced != null ? <b>{hrsFmt(x.serviced)} h</b> : <b style={{ color: WARN }}>— nothing on file</b>}{x.dos ? ` on ${svcNormDos(x.dos)}` : ''}
                    {x.serviceRule && x.serviceRule.interval ? ` · every ${intFmt(x.serviceRule.interval)} h` : ''}
                  </div>
                  <div style={muted}>{isSilent
                    ? `Tracker silent — measured against the last reading typed in Weekly Inventory${x.known != null ? ` (${hrsFmt(x.known)} h)` : ' (none on file)'}.`
                    : `Measured against the factory meter${d.engineHours != null ? ` (${hrsFmt(d.engineHours)} h now)` : ''}, not the last typed reading.`}</div>
                  {x.note ? <div style={{ ...muted, marginTop: 3 }}>Note on file: <span style={{ color: TEXT }}>{x.note}</span></div> : null}
                </div>
              ) : <div style={{ ...small, color: STEEL, marginTop: 4 }}>No service schedule in DOPE for this unit.</div>}
              {onOpenMaint ? <button type="button" onClick={() => onOpenMaint(x.r.unit)} style={{ ...btn, marginTop: 9 }}>✓ Log a service in Maintenance →</button> : null}
            </div>

            {pm ? (
              <div style={block}>
                <div style={lbl}>{maker}'s own service log</div>
                <div style={{ ...small, color: toneColor[svc.tone] === STEEL ? TEXT : toneColor[svc.tone], fontWeight: svc.tone === 'bad' || svc.tone === 'warn' ? 800 : 500, marginTop: 3 }}>{svc.text || 'Nothing scheduled'}</div>
                <div style={muted}>
                  {pm.dueDate ? `${maker}'s due date ${mdy(pm.dueDate)} · ` : ''}
                  {pm.lastHours != null ? `last service logged with ${maker}: ${intFmt(pm.lastHours)} h${pm.lastDate ? ' on ' + mdy(pm.lastDate) : ''}` : `no service logged with ${maker}`}
                  {Number(pm.done) > 0 ? ` · ${intFmt(pm.done)} logged in all` : ''}
                </div>
                <div style={muted}>{maker} only knows services entered with them. DOPE's Service Due above is the one the shop runs by.</div>
              </div>
            ) : null}

            <div style={{ ...block, ...(shopRow ? { borderLeft: `3px solid ${WARN}` } : {}) }}>
              <div style={lbl}>Shop</div>
              {shopRow ? (
                <div>
                  <div style={{ ...big, color: WARN }}>{shopRow.thirdParty ? 'At a 3rd-party shop' : 'In the shop now'} — day {intFmt(shopRow.days)}</div>
                  <div style={small}>{shopRow.diagnosis ? <span>Diagnosis: <b>{shopRow.diagnosis}</b></span> : <span style={{ color: STEEL }}>No diagnosis typed yet</span>}</div>
                  <div style={muted}>{[shopRow.dateIn ? `in ${dayShort(shopRow.dateIn)}` : '', shopRow.mechanic ? `mechanic ${shopRow.mechanic}` : '', shopRow.estOut ? `est. out ${dayShort(shopRow.estOut)}` : ''].filter(Boolean).join(' · ')}</div>
                  {onOpenInShop ? <button type="button" onClick={() => onOpenInShop(x.r.unit)} style={{ ...btn, marginTop: 8 }}>Open it in In Shop →</button> : null}
                </div>
              ) : <div style={{ ...small, color: GOOD, marginTop: 3 }}>Not in the shop.</div>}
              <div style={{ ...lbl, marginTop: 10 }}>Past shop visits{visits.length ? ` · ${visits.length}` : ''}</div>
              {visits.length ? visits.slice(0, 8).map((r, i) => (
                <div key={`${r.dateIn}-${r.completedAt || i}`} style={{ ...row, ...(i === 0 ? { borderTop: 'none' } : {}) }}>
                  <div style={{ flex: '0 0 auto', fontSize: 11.5, fontWeight: 800, color: TEXT, whiteSpace: 'nowrap' }}>{dayShort(r.dateIn)}{r.dateOut && r.dateOut !== r.dateIn ? ` → ${dayShort(r.dateOut)}` : ''}</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={small}>{r.diagnosis || <span style={{ color: STEEL }}>no diagnosis typed</span>}</div>
                    <div style={muted}>{[r.days ? `${intFmt(r.days)} day${Number(r.days) === 1 ? '' : 's'} down` : '', r.mechanic ? `mechanic ${r.mechanic}` : ''].filter(Boolean).join(' · ')}</div>
                  </div>
                </div>
              )) : <div style={{ ...muted, marginTop: 3 }}>None on file. Shop History starts when a unit is marked complete on In Shop.</div>}
              {visits.length > 8 ? <div style={muted}>+ {visits.length - 8} older in Shop History.</div> : null}
            </div>

            <div style={block}>
              <div style={lbl}>Service history{history.length ? ` · ${history.length}` : ''}</div>
              {log == null ? <div style={{ ...muted, marginTop: 3 }}>Reading…</div>
                : history.length ? history.slice(0, 8).map((e, i) => (
                  <div key={e.id || `${e.at}-${i}`} style={{ ...row, ...(i === 0 ? { borderTop: 'none' } : {}) }}>
                    <div style={{ flex: '0 0 auto', fontSize: 11.5, fontWeight: 800, color: TEXT, whiteSpace: 'nowrap' }}>{e.dos || '—'}</div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={small}>{e.serviced != null ? <b>{hrsFmt(e.serviced)} h</b> : '—'}{e.work ? ` — ${e.work}` : ''}</div>
                      <div style={muted}>{[e.shop ? `shop: ${e.shop}` : (e.mechanic ? `by ${e.mechanic}` : ''), e.via === 'weekly' ? 'entered in Weekly Inventory' : ''].filter(Boolean).join(' · ')}</div>
                    </div>
                  </div>
                )) : <div style={{ ...muted, marginTop: 3 }}>Nothing logged in DOPE yet for {x.r.unit}. History starts with the first ✓ Serviced saved in Maintenance or the first Serviced / DOS change in Weekly Inventory.</div>}
            </div>

            {/* #FLEET_WO_TABLES_V1 — work orders + every PO on this unit (live logs and Bob's history), service, shop visits, DVIRs */}
            {sg && x.r && x.r.unit ? <div style={block}><UnitWorkHistory sg={sg} unit={x.r.unit} theme={{ text: TEXT, muted: STEEL, line: LINE, accent: ACCENT, gold: WARN, good: GOOD }} /></div> : null}

            {d.warrantyEnd ? (
              <div style={{ ...block, ...(warDays != null && warDays >= 0 && warDays <= 90 ? { borderLeft: `3px solid ${WARN}` } : {}) }}>
                <div style={lbl}>Warranty</div>
                <div style={{ ...small, marginTop: 3, color: warDays != null && warDays >= 0 && warDays <= 90 ? WARN : TEXT, fontWeight: warDays != null && warDays >= 0 && warDays <= 90 ? 800 : 500 }}>
                  {warDays != null && warDays < 0 ? `Ended ${mdy(d.warrantyEnd)}` : `Runs to ${mdy(d.warrantyEnd)}${warDays != null ? ` — ${intFmt(warDays)} days left` : ''}`}</div>
                {warDays != null && warDays >= 0 && flt.total > 0 ? <div style={muted}>A code is open while the rig is still under warranty.</div> : null}
              </div>
            ) : null}
          </div>
        </div>

        {/* where to go to do something about it */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 4, paddingTop: 12, borderTop: `1px solid ${LINE}` }}>
          {onOpenMaint ? <button type="button" onClick={() => onOpenMaint(x.r.unit)} style={btn}>Maintenance →</button> : null}
          {onOpenInShop ? <button type="button" onClick={() => onOpenInShop(shopRow ? x.r.unit : '')} style={btn}>In Shop →</button> : null}
          {onOpenWeekly ? <button type="button" onClick={onOpenWeekly} style={btn}>Weekly Inventory →</button> : null}
          <span style={{ ...muted, marginLeft: 'auto' }}>Read-only · {maker} feed checked {whenFmt(pulledAt) || '—'}</span>
        </div>
      </div>
    </div>
  );
}
